import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Lock, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { schoolInfo } from '../utils/mock';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const VerifyOTPPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const mobile = location.state?.mobile;
  
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(300); // 5 minutes in seconds

  useEffect(() => {
    if (!mobile) {
      navigate('/login');
      return;
    }

    // Get demo OTP from session storage
    const demoOTP = sessionStorage.getItem('demo_otp');
    if (demoOTP) {
      // Show OTP in toast for easy access
      toast.info('Test OTP', {
        description: `Your OTP: ${demoOTP}`,
        duration: 300000 // 5 minutes
      });
    }

    // Start countdown timer
    const countdown = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(countdown);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(countdown);
  }, [mobile, navigate]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validate OTP
    if (otp.length !== 6 || !/^\d+$/.test(otp)) {
      setError('Please enter a valid 6-digit OTP');
      setLoading(false);
      return;
    }

    try {
      // Verify OTP using Twilio API
      const response = await axios.post(`${BACKEND_URL}/api/otp/verify-otp`, {
        mobile_number: mobile,
        otp: otp
      });

      if (response.data.valid) {
        // OTP verified successfully
        const parentData = {
          mobile: mobile,
          mobile_number: mobile
        };
        
        // Get student data from session storage
        const studentDataStr = sessionStorage.getItem('studentData');
        if (studentDataStr) {
          const studentData = JSON.parse(studentDataStr);
          parentData.student = studentData;
        }
        
        // Store parent data in localStorage
        localStorage.setItem('parentData', JSON.stringify(parentData));
        localStorage.setItem('isParentAuthenticated', 'true');

        // Track login for analytics
        try {
          await axios.post(`${BACKEND_URL}/api/login/track`, null, {
            params: {
              mobile_number: mobile,
              user_type: 'parent'
            }
          });
        } catch (trackErr) {
          console.warn('Failed to track login:', trackErr);
          // Don't block login if tracking fails
        }

        toast.success('Login Successful!', {
          description: 'Welcome to AMTEE Public School'
        });

        setTimeout(() => {
          navigate('/dashboard');
        }, 500);
      } else {
        setError('Invalid or expired OTP. Please try again.');
        setLoading(false);
      }

    } catch (err) {
      console.error('OTP verification error:', err);
      const errorMsg = err.response?.data?.detail || 'Invalid or expired OTP. Please try again.';
      setError(errorMsg);
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/otp/send-otp`, {
        mobile_number: mobile
      });

      if (response.data.status === 'success') {
        toast.success('OTP Resent!', {
          description: `A new OTP has been sent to +91${mobile}`
        });
        setTimer(300); // Reset timer to 5 minutes
        setOtp('');
        setError('');
      }
    } catch (err) {
      console.error('Resend OTP error:', err);
      toast.error('Failed to resend OTP', {
        description: 'Please try again or go back to login'
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#4A90E2]/10 via-white to-[#4A90E2]/5 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img 
            src={schoolInfo.logo} 
            alt="AMTEE PUBLIC SCHOOL" 
            className="h-24 w-24 mx-auto mb-4 object-contain"
          />
          <h1 className="text-2xl font-bold text-gray-900">Verify OTP</h1>
          <p className="text-gray-600">Enter the OTP sent to {mobile}</p>
        </div>

        <Card className="shadow-2xl border-0">
          <CardHeader>
            <CardTitle className="text-2xl font-bold">Enter OTP</CardTitle>
            <CardDescription>
              We've sent a 6-digit OTP to +91{mobile} via SMS
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleVerifyOTP} className="space-y-6">
              {/* Demo OTP Display for Testing */}
              {sessionStorage.getItem('demo_otp') && (
                <Alert className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
                  <AlertDescription className="text-center">
                    <p className="text-sm font-semibold text-gray-700 mb-2">Testing Mode - OTP</p>
                    <p className="text-4xl font-bold text-blue-600 tracking-widest mb-2">
                      {sessionStorage.getItem('demo_otp')}
                    </p>
                    <p className="text-xs text-gray-600">
                      This OTP is shown for testing purposes only
                    </p>
                  </AlertDescription>
                </Alert>
              )}

              <div>
                <Label htmlFor="otp" className="text-base">One-Time Password</Label>
                <div className="relative mt-2">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    id="otp"
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    placeholder="Enter 6-digit OTP"
                    className="pl-10 py-6 text-lg text-center tracking-widest"
                    maxLength="6"
                    required
                    autoFocus
                  />
                </div>
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {timer > 0 ? (
                <div className="text-center">
                  <p className="text-sm text-gray-600">
                    OTP expires in: <span className="font-semibold text-[#4A90E2]">{formatTime(timer)}</span>
                  </p>
                </div>
              ) : (
                <Alert>
                  <AlertDescription>
                    OTP expired. Please request a new OTP.
                  </AlertDescription>
                </Alert>
              )}

              <Button 
                type="submit" 
                className="w-full bg-[#4A90E2] hover:bg-[#357ABD] py-6 text-lg"
                disabled={loading || timer === 0}
              >
                {loading ? 'Verifying...' : 'Verify & Login'}
                <CheckCircle2 className="ml-2 h-5 w-5" />
              </Button>
            </form>

            <div className="mt-6 flex items-center justify-between">
              <Button
                variant="link"
                onClick={() => navigate('/login')}
                className="text-gray-600 hover:text-[#4A90E2] p-0"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Change Number
              </Button>

              <Button
                variant="link"
                onClick={handleResendOTP}
                className="text-[#4A90E2] hover:text-[#357ABD] p-0"
              >
                Resend OTP
              </Button>
            </div>

            <div className="mt-6 p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-gray-700">
                <span className="font-semibold">Didn't receive OTP?</span>
              </p>
              <p className="text-xs text-gray-600 mt-1">
                Check your SMS inbox or click "Resend OTP" to receive a new code.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default VerifyOTPPage;
